import type { Risk, RiskCategory, RiskModelType } from "@unbox/db";
import { ShieldZap } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useAllRisks } from "@/api/admin/risks";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { CATEGORY_LABELS, tierToSeverityLabel } from "@/components/safety-config/risk-groups";
import { MODEL_TYPE_LABELS } from "@/pages/admin/merchant-detail/tabs/risk-config-v2/config-fields";
import { CreateRiskModal, EditRiskModal } from "@/pages/admin/modals/risk-catalog-modal";
import { PageHeader } from "@/pages/admin/page-header";

export const RisksPage = () => {
  const { data: risks, isLoading } = useAllRisks();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const list = (risks as Risk[] | undefined) ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.id.toLowerCase().includes(q) ||
        r.readableName.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q),
    );
  }, [risks, search]);

  return (
    <div className="flex flex-col gap-6 px-12 pb-8 overflow-auto">
      <PageHeader
        title="Risk Catalog"
        subtitle="All risks available across merchants. Add, edit, or remove risks and toggle vision."
        icon={ShieldZap}
        count={filtered.length}
        search={search}
        onSearchChange={setSearch}
        actions={<CreateRiskModal />}
      />

      <TableCard.Root>
        <Table aria-label="Risk catalog">
          <Table.Header>
            <Table.Head id="id" label="ID" isRowHeader className="w-full" />
            <Table.Head id="name" label="Name" className="w-[260px]" />
            <Table.Head id="category" label="Category" className="w-[180px]" />
            <Table.Head id="modelType" label="Model" className="w-[120px]" />
            <Table.Head id="tier" label="Default severity" className="w-[110px]" />
            <Table.Head id="vision" label="Vision" className="w-[90px]" />
            <Table.Head id="actions" label="" className="w-min-0" />
          </Table.Header>
          <Table.Body items={filtered}>
            {(risk) => (
              <Table.Row id={risk.id}>
                <Table.Cell className="font-medium text-primary font-mono text-xs">{risk.id}</Table.Cell>
                <Table.Cell className="text-tertiary">{risk.readableName}</Table.Cell>
                <Table.Cell>
                  <Badge size="sm" color="gray">
                    {CATEGORY_LABELS[risk.category as RiskCategory] ?? risk.category}
                  </Badge>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {MODEL_TYPE_LABELS[risk.modelType as RiskModelType] ?? risk.modelType}
                </Table.Cell>
                <Table.Cell>{tierToSeverityLabel(risk.defaultTier)}</Table.Cell>
                <Table.Cell>
                  <Badge size="sm" color={risk.vision ? "success" : "gray"}>
                    {risk.vision ? "On" : "Off"}
                  </Badge>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <EditRiskModal risk={risk} />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        {!isLoading && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-tertiary">No risks found.</div>
        )}
      </TableCard.Root>
    </div>
  );
};
